/**
 * Document operations — Node.js file system operations.
 *
 * These functions require Node.js built-ins (fs, path) and are not browser-safe.
 * Browser consumers should use the Blob-based uploadFile/uploadFiles from './documents.js'.
 */

import { execSync } from 'node:child_process'
import fs from 'node:fs'
import os from 'node:os'
import path from 'node:path'
import type { ArbiClient } from '@arbidocs/client'
import {
  uploadFile,
  uploadFiles,
  uploadDocumentsDirect,
  mimeFromName,
  SUPPORTED_EXTENSIONS,
  sanitizeFolderPath,
  type DirectUploadFile,
  type UploadDirectOptions,
  type UploadDirectResult,
  type UploadOptions,
  type UploadResult,
  type UploadBatchResult,
  type SkippedFile,
} from './documents.js'
import type { AuthHeaders } from '../fetch.js'

// Re-export for CLI/manifest consumers that need to filter inputs upfront.
export { SUPPORTED_EXTENSIONS }

// ── Constants ──────────────────────────────────────────────────────────────

/** Max files per HTTP request — matches the frontend BATCH_SIZE. */
const UPLOAD_BATCH_SIZE = 100

/** Max file size — matches nginx client_max_body_size (10 GB). */
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024 * 1024

/** Node.js Buffer hard limit (~2 GiB). Files above this need streaming. */
const NODE_BUFFER_LIMIT = 2 * 1024 * 1024 * 1024 - 1

/** Archive extensions that can be extracted via the system `7z` command. */
export const ARCHIVE_EXTENSIONS = new Set(['.7z', '.rar'])

// ── Progress callback types ────────────────────────────────────────────────

export interface UploadProgress {
  /** Current batch number (1-based). */
  batch: number
  /** Total number of batches. */
  totalBatches: number
  /** Folder being uploaded. */
  folder: string
  /** Number of files in this batch. */
  filesInBatch: number
  /** Cumulative files uploaded so far (including this batch). */
  filesUploaded: number
  /** Total files to upload. */
  totalFiles: number
}

export interface UploadDirectoryOptions {
  /** Called before each batch is sent. */
  onBatchStart?: (progress: UploadProgress) => void
  /** Called after each batch completes. */
  onBatchComplete?: (progress: UploadProgress & { result: UploadResult }) => void
  /** Config ext_id to pass to each upload batch (e.g. for SKIP_DUPLICATES). */
  configExtId?: string
}

// ── Internal helpers ────────────────────────────────────────────────────────

interface CollectedFile {
  absolutePath: string
  relativePath: string
  size: number
}

/**
 * Recursively collect files from a directory, filtered by SUPPORTED_EXTENSIONS.
 */
function collectFiles(dirPath: string, baseDir?: string): CollectedFile[] {
  const root = baseDir ?? dirPath
  const results: CollectedFile[] = []

  for (const entry of fs.readdirSync(dirPath, { withFileTypes: true })) {
    const fullPath = path.join(dirPath, entry.name)
    if (entry.isDirectory()) {
      results.push(...collectFiles(fullPath, root))
    } else if (entry.isFile()) {
      const ext = path.extname(entry.name).toLowerCase()
      if (SUPPORTED_EXTENSIONS.has(ext)) {
        const stat = fs.statSync(fullPath)
        results.push({
          absolutePath: fullPath,
          relativePath: path.relative(root, fullPath),
          size: stat.size,
        })
      }
    }
  }

  return results
}

/**
 * Split an array into chunks of at most `size` elements.
 */
function chunk<T>(arr: T[], size: number): T[][] {
  const chunks: T[][] = []
  for (let i = 0; i < arr.length; i += size) {
    chunks.push(arr.slice(i, i + size))
  }
  return chunks
}

// ── Node.js file system operations ──────────────────────────────────────────

/**
 * Read a file into a Blob, using chunked reads for files > 2 GB.
 *
 * When `fileName` is provided, its extension is used to set the Blob's
 * `type` so multipart uploads carry the correct Content-Type per file.
 * Without a type, Node's Blob has `type === ''` and FormData encodes the
 * part as `application/octet-stream`.
 */
function readFileAsBlob(filePath: string, fileSize: number, fileName?: string): Blob {
  const type = fileName ? (mimeFromName(fileName) ?? '') : ''
  if (fileSize > NODE_BUFFER_LIMIT) {
    const chunks: Uint8Array[] = []
    const CHUNK = 256 * 1024 * 1024
    const fd = fs.openSync(filePath, 'r')
    let pos = 0
    while (pos < fileSize) {
      const len = Math.min(CHUNK, fileSize - pos)
      const buf = Buffer.alloc(len)
      fs.readSync(fd, buf, 0, len, pos)
      chunks.push(new Uint8Array(buf))
      pos += len
    }
    fs.closeSync(fd)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return new Blob(chunks as any[], { type })
  }
  return new Blob([fs.readFileSync(filePath)], { type })
}

/**
 * Upload a local file by path. Handles readFileSync + basename + Blob + uploadFile.
 * Convenience wrapper for CLI/TUI consumers that work with file system paths.
 *
 * Workspace context is derived server-side from the open session (the caller's
 * JWT + most recent ``/workspace/{id}/open`` call), so no workspace id is sent.
 */
export async function uploadLocalFile(
  auth: AuthHeaders,
  filePath: string,
  options?: UploadOptions
): Promise<UploadResult & { fileName: string }> {
  const stat = fs.statSync(filePath)
  const fileName = path.basename(filePath)
  const blob = readFileAsBlob(filePath, stat.size, fileName)
  const result = await uploadFile(auth, blob, fileName, options)
  return { ...result, fileName }
}

/**
 * Upload a flat list of local files via the direct-to-MinIO flow.
 *
 * This is the Node-side convenience wrapper around ``uploadDocumentsDirect``
 * for CLI/TUI consumers that work with filesystem paths. It reads each file
 * fully into memory and delegates to the browser-safe helper for hashing,
 * encryption, and transport.
 *
 * Callers are responsible for supplying the raw 32-byte workspace key. In
 * the CLI this is unwrapped lazily from the stored signing key + workspace
 * ``wrapped_key``; see ``getRawWorkspaceKey`` in ``auth.ts``.
 */
export async function uploadLocalFilesDirect(
  arbi: ArbiClient,
  workspaceKey: Uint8Array,
  filePaths: string[],
  options?: UploadDirectOptions
): Promise<UploadDirectResult> {
  const files: DirectUploadFile[] = filePaths.map((fp) => {
    const resolved = path.resolve(fp)
    const stat = fs.statSync(resolved)
    if (stat.size > NODE_BUFFER_LIMIT) {
      throw new Error(
        `File ${fp} is ${stat.size} bytes — direct upload helper currently loads the full ` +
          `file into memory; use chunked upload for files > 2 GiB.`
      )
    }
    return {
      name: path.basename(resolved),
      data: new Uint8Array(fs.readFileSync(resolved)),
      contentType: mimeFromName(path.basename(resolved)),
    }
  })
  return uploadDocumentsDirect(arbi, workspaceKey, files, options)
}

// ── Direct-to-MinIO: directory / zip / archive helpers ────────────────────

/**
 * Options for the direct-upload directory/zip/archive helpers. Mirrors the
 * legacy ``UploadDirectoryOptions`` so CLI consumers can switch between the
 * two flows without rewiring their progress callbacks. ``configExtId`` is
 * forwarded to ``uploadDocumentsDirect`` for SKIP_DUPLICATES support.
 */
export interface UploadDirectoryDirectOptions {
  onBatchStart?: (progress: UploadProgress) => void
  onBatchComplete?: (
    progress: UploadProgress & {
      result: { doc_ext_ids?: string[]; skipped?: SkippedFile[] }
    }
  ) => void
  configExtId?: string
}

interface PreparedDirectGroup {
  folder: string
  files: Array<{ name: string; data: Uint8Array; contentType?: string }>
}

/** Shared runner: take pre-grouped direct-upload entries and push each group. */
async function runGroupedDirectUpload(
  arbi: ArbiClient,
  workspaceKey: Uint8Array,
  groups: PreparedDirectGroup[],
  options?: UploadDirectoryDirectOptions
): Promise<UploadBatchResult> {
  const allDocIds: string[] = []
  const allSkipped: SkippedFile[] = []
  const folders = new Map<
    string,
    { fileCount: number; doc_ext_ids: string[]; skipped: SkippedFile[] }
  >()

  const totalFiles = groups.reduce((s, g) => s + g.files.length, 0)
  const totalBatches = groups.length
  let currentBatch = 0
  let filesUploaded = 0

  for (const group of groups) {
    currentBatch++
    const progress: UploadProgress = {
      batch: currentBatch,
      totalBatches,
      folder: group.folder || '<root>',
      filesInBatch: group.files.length,
      filesUploaded,
      totalFiles,
    }
    options?.onBatchStart?.(progress)

    const result = await uploadDocumentsDirect(arbi, workspaceKey, group.files, {
      folder: group.folder || undefined,
      configExtId: options?.configExtId,
    })

    const skipped = result.skipped ?? []
    allDocIds.push(...result.doc_ext_ids)
    allSkipped.push(...skipped)
    folders.set(group.folder, {
      fileCount: group.files.length,
      doc_ext_ids: result.doc_ext_ids,
      skipped,
    })

    filesUploaded += group.files.length
    options?.onBatchComplete?.({
      ...progress,
      filesUploaded,
      result: { doc_ext_ids: result.doc_ext_ids, skipped },
    })
  }

  return { doc_ext_ids: allDocIds, skipped: allSkipped, folders }
}

/**
 * Walk a directory, group supported files by their relative subfolder (with
 * the directory basename as the root), and upload each group via the
 * direct-to-MinIO flow.
 */
export async function uploadLocalDirectoryDirect(
  arbi: ArbiClient,
  workspaceKey: Uint8Array,
  dirPath: string,
  options?: UploadDirectoryDirectOptions
): Promise<UploadBatchResult> {
  const resolvedDir = path.resolve(dirPath)
  const dirName = path.basename(resolvedDir)
  const entries = collectFiles(resolvedDir)
  if (entries.length === 0) {
    return { doc_ext_ids: [], skipped: [], folders: new Map() }
  }

  const buckets = new Map<string, Array<{ name: string; data: Uint8Array; contentType?: string }>>()
  for (const entry of entries) {
    if (entry.size > NODE_BUFFER_LIMIT) {
      throw new Error(
        `File ${entry.absolutePath} is ${entry.size} bytes — direct upload currently loads ` +
          `the full file into memory; skip --s3 for files > 2 GiB.`
      )
    }
    const relDir = path.dirname(entry.relativePath)
    const folder = sanitizeFolderPath(relDir === '.' ? dirName : `${dirName}/${relDir}`)
    const name = path.basename(entry.relativePath)
    if (!buckets.has(folder)) buckets.set(folder, [])
    buckets.get(folder)!.push({
      name,
      data: new Uint8Array(fs.readFileSync(entry.absolutePath)),
      contentType: mimeFromName(name),
    })
  }

  const groups: PreparedDirectGroup[] = []
  for (const [folder, files] of buckets) groups.push({ folder, files })
  return runGroupedDirectUpload(arbi, workspaceKey, groups, options)
}

/**
 * Extract a zip archive in memory, group supported entries by their internal
 * folder structure, and upload each group via the direct-to-MinIO flow. Uses
 * jszip via dynamic import so the dep is optional at load time. Mirrors the
 * single-root detection behavior of ``uploadZip``.
 */
export async function uploadLocalZipDirect(
  arbi: ArbiClient,
  workspaceKey: Uint8Array,
  zipPath: string,
  options?: UploadDirectoryDirectOptions
): Promise<UploadBatchResult> {
  const JSZip = (await import('jszip')).default
  const zipBuffer = fs.readFileSync(zipPath)
  const zip = await JSZip.loadAsync(zipBuffer)
  const zipBaseName = path.basename(zipPath).replace(/\.zip$/i, '')

  const entries: Array<{
    zipEntryPath: string
    zipEntry: { async: (t: 'uint8array') => Promise<Uint8Array> }
  }> = []
  zip.forEach((relativePath, zipEntry) => {
    if (zipEntry.dir) return
    const ext = path.extname(relativePath).toLowerCase()
    if (SUPPORTED_EXTENSIONS.has(ext)) {
      entries.push({ zipEntryPath: relativePath, zipEntry })
    }
  })

  if (entries.length === 0) {
    return { doc_ext_ids: [], skipped: [], folders: new Map() }
  }

  // Detect a single top-level folder to avoid nesting the zip base name under
  // its own root (matches the legacy multipart flow).
  const firstParts = new Set(entries.map((e) => e.zipEntryPath.split('/')[0]))
  const hasSingleRoot = firstParts.size === 1 && entries.every((e) => e.zipEntryPath.includes('/'))

  const buckets = new Map<string, Array<{ name: string; data: Uint8Array; contentType?: string }>>()
  for (const entry of entries) {
    const data = await entry.zipEntry.async('uint8array')
    const fileName = path.basename(entry.zipEntryPath)
    const relDir = path.dirname(entry.zipEntryPath)
    let folder: string
    if (hasSingleRoot) {
      folder = relDir === '.' ? zipBaseName : relDir
    } else {
      folder = relDir === '.' ? zipBaseName : `${zipBaseName}/${relDir}`
    }
    folder = sanitizeFolderPath(folder)
    if (!buckets.has(folder)) buckets.set(folder, [])
    buckets.get(folder)!.push({
      name: fileName,
      data,
      contentType: mimeFromName(fileName),
    })
  }

  const groups: PreparedDirectGroup[] = []
  for (const [folder, files] of buckets) groups.push({ folder, files })
  return runGroupedDirectUpload(arbi, workspaceKey, groups, options)
}

/**
 * Upload a 7z/rar archive via the direct-to-MinIO flow. Shells out to the
 * system ``7z`` binary to extract to a temporary directory, then delegates
 * to ``uploadLocalDirectoryDirect``. The temp directory is removed on exit.
 */
export async function uploadLocalArchiveDirect(
  arbi: ArbiClient,
  workspaceKey: Uint8Array,
  archivePath: string,
  options?: UploadDirectoryDirectOptions
): Promise<UploadBatchResult> {
  try {
    execSync('7z --help', { stdio: 'ignore' })
  } catch {
    throw new Error('7z is not installed. Install p7zip-full to extract archives.')
  }

  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'arbi-archive-'))
  let uploadDir: string | undefined
  let namedDir: string | undefined
  try {
    execSync(`7z x -y -o"${tmpDir}" "${path.resolve(archivePath)}"`, { stdio: 'ignore' })

    const topLevel = fs.readdirSync(tmpDir)
    if (topLevel.length === 1 && fs.statSync(path.join(tmpDir, topLevel[0])).isDirectory()) {
      uploadDir = path.join(tmpDir, topLevel[0])
    } else {
      const baseName = path.basename(archivePath).replace(/\.(7z|rar|zip)$/i, '')
      namedDir = path.join(path.dirname(tmpDir), baseName)
      fs.renameSync(tmpDir, namedDir)
      uploadDir = namedDir
    }

    return await uploadLocalDirectoryDirect(arbi, workspaceKey, uploadDir, options)
  } finally {
    // Clean up whichever temp directory still exists.
    if (namedDir && fs.existsSync(namedDir)) {
      fs.rmSync(namedDir, { recursive: true, force: true })
    }
    if (fs.existsSync(tmpDir)) {
      fs.rmSync(tmpDir, { recursive: true, force: true })
    }
  }
}

/**
 * Upload all supported files from a directory, preserving folder structure.
 * Files are grouped by their relative folder path, then each group is split
 * into batches of UPLOAD_BATCH_SIZE to avoid overwhelming the backend.
 */
export async function uploadDirectory(
  auth: AuthHeaders,
  dirPath: string,
  options?: UploadDirectoryOptions
): Promise<UploadBatchResult> {
  const resolvedDir = path.resolve(dirPath)
  const dirName = path.basename(resolvedDir)
  const entries = collectFiles(resolvedDir)

  if (entries.length === 0) {
    return { doc_ext_ids: [], skipped: [], folders: new Map() }
  }

  // Group by folder
  const groups = new Map<string, Array<{ absolutePath: string; name: string; size: number }>>()
  for (const entry of entries) {
    const relDir = path.dirname(entry.relativePath)
    // Prepend the directory name so the root folder appears on the backend
    const folder = relDir === '.' ? dirName : `${dirName}/${relDir}`
    if (!groups.has(folder)) groups.set(folder, [])
    groups.get(folder)!.push({
      absolutePath: entry.absolutePath,
      name: path.basename(entry.relativePath),
      size: entry.size,
    })
  }

  const allDocIds: string[] = []
  const allSkipped: SkippedFile[] = []
  const folders = new Map<
    string,
    { fileCount: number; doc_ext_ids: string[]; skipped: SkippedFile[] }
  >()

  // Count total batches across all folders for progress reporting
  let totalBatches = 0
  const folderBatches = new Map<
    string,
    Array<Array<{ absolutePath: string; name: string; size: number }>>
  >()
  for (const [folder, files] of groups) {
    const batches = chunk(files, UPLOAD_BATCH_SIZE)
    folderBatches.set(folder, batches)
    totalBatches += batches.length
  }

  let currentBatch = 0
  let filesUploaded = 0

  for (const [folder, batches] of folderBatches) {
    const folderDocIds: string[] = []
    const folderSkipped: SkippedFile[] = []
    const folderFileCount = batches.reduce((sum, b) => sum + b.length, 0)

    for (const batch of batches) {
      currentBatch++

      const progress: UploadProgress = {
        batch: currentBatch,
        totalBatches,
        folder,
        filesInBatch: batch.length,
        filesUploaded,
        totalFiles: entries.length,
      }

      options?.onBatchStart?.(progress)

      // Separate oversized files and read the rest into memory
      const uploadable: Array<{ data: Blob; name: string }> = []
      const batchSkipped: SkippedFile[] = []
      for (const f of batch) {
        if (f.size > MAX_FILE_SIZE_BYTES) {
          const sizeGB = (f.size / (1024 * 1024 * 1024)).toFixed(2)
          batchSkipped.push({
            file_name: f.name,
            reason: `File too large (${sizeGB} GB) — exceeds 10 GB upload limit`,
          })
        } else {
          try {
            uploadable.push({ data: readFileAsBlob(f.absolutePath, f.size, f.name), name: f.name })
          } catch (err) {
            batchSkipped.push({
              file_name: f.name,
              reason: `Read error: ${err instanceof Error ? err.message : String(err)}`,
            })
          }
        }
      }

      let result: UploadResult = { doc_ext_ids: [], skipped: [] }
      if (uploadable.length > 0) {
        result = await uploadFiles(auth, uploadable, {
          folder,
          configExtId: options?.configExtId,
        })
      }
      const skipped = [...batchSkipped, ...(result.skipped ?? [])]

      folderDocIds.push(...(result.doc_ext_ids ?? []))
      folderSkipped.push(...skipped)

      filesUploaded += batch.length

      options?.onBatchComplete?.({
        ...progress,
        filesUploaded,
        result,
      })
    }

    allDocIds.push(...folderDocIds)
    allSkipped.push(...folderSkipped)
    folders.set(folder, {
      fileCount: folderFileCount,
      doc_ext_ids: folderDocIds,
      skipped: folderSkipped,
    })
  }

  return { doc_ext_ids: allDocIds, skipped: allSkipped, folders }
}

/**
 * Upload files from a zip archive, preserving internal folder structure.
 * Uses jszip via dynamic import so the dependency is optional at load time.
 * Files are uploaded in batches of UPLOAD_BATCH_SIZE per folder.
 */
export async function uploadZip(
  auth: AuthHeaders,
  zipPath: string,
  options?: UploadDirectoryOptions
): Promise<UploadBatchResult> {
  const JSZip = (await import('jszip')).default
  const zipBuffer = fs.readFileSync(zipPath)
  const zip = await JSZip.loadAsync(zipBuffer)
  const zipBaseName = path.basename(zipPath).replace(/\.zip$/i, '')

  // Collect supported entries
  const entries: Array<{
    zipEntryPath: string
    zipEntry: { async: (t: 'uint8array') => Promise<Uint8Array> }
  }> = []
  zip.forEach((relativePath, zipEntry) => {
    if (zipEntry.dir) return
    const ext = path.extname(relativePath).toLowerCase()
    if (SUPPORTED_EXTENSIONS.has(ext)) {
      entries.push({ zipEntryPath: relativePath, zipEntry })
    }
  })

  if (entries.length === 0) {
    return { doc_ext_ids: [], skipped: [], folders: new Map() }
  }

  // Detect single root folder to avoid redundant nesting
  const firstParts = new Set(entries.map((e) => e.zipEntryPath.split('/')[0]))
  const hasSingleRoot = firstParts.size === 1 && entries.every((e) => e.zipEntryPath.includes('/'))

  // Group by folder and extract data
  const groups = new Map<string, Array<{ name: string; data: Uint8Array }>>()
  for (const entry of entries) {
    const data = await entry.zipEntry.async('uint8array')
    const fileName = path.basename(entry.zipEntryPath)
    const relDir = path.dirname(entry.zipEntryPath)

    let folder: string
    if (hasSingleRoot) {
      // Use the zip internal path as-is (root folder already present)
      folder = relDir === '.' ? zipBaseName : relDir
    } else {
      // Prepend zip base name
      folder = relDir === '.' ? zipBaseName : `${zipBaseName}/${relDir}`
    }
    folder = sanitizeFolderPath(folder)

    if (!groups.has(folder)) groups.set(folder, [])
    groups.get(folder)!.push({ name: fileName, data })
  }

  const allDocIds: string[] = []
  const allSkipped: SkippedFile[] = []
  const folders = new Map<
    string,
    { fileCount: number; doc_ext_ids: string[]; skipped: SkippedFile[] }
  >()

  // Count total batches for progress
  let totalBatches = 0
  const folderBatches = new Map<string, Array<Array<{ name: string; data: Uint8Array }>>>()
  for (const [folder, files] of groups) {
    const batches = chunk(files, UPLOAD_BATCH_SIZE)
    folderBatches.set(folder, batches)
    totalBatches += batches.length
  }

  let currentBatch = 0
  let filesUploaded = 0

  for (const [folder, batches] of folderBatches) {
    const folderDocIds: string[] = []
    const folderSkipped: SkippedFile[] = []
    const folderFileCount = batches.reduce((sum, b) => sum + b.length, 0)

    for (const batch of batches) {
      currentBatch++

      const progress: UploadProgress = {
        batch: currentBatch,
        totalBatches,
        folder,
        filesInBatch: batch.length,
        filesUploaded,
        totalFiles: entries.length,
      }

      options?.onBatchStart?.(progress)

      const blobs = batch.map((f) => ({
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: new Blob([f.data as any]),
        name: f.name,
      }))

      const result = await uploadFiles(auth, blobs, {
        folder,
        configExtId: options?.configExtId,
      })
      const skipped = result.skipped ?? []

      folderDocIds.push(...(result.doc_ext_ids ?? []))
      folderSkipped.push(...skipped)

      filesUploaded += batch.length

      options?.onBatchComplete?.({
        ...progress,
        filesUploaded,
        result,
      })
    }

    allDocIds.push(...folderDocIds)
    allSkipped.push(...folderSkipped)
    folders.set(folder, {
      fileCount: folderFileCount,
      doc_ext_ids: folderDocIds,
      skipped: folderSkipped,
    })
  }

  return { doc_ext_ids: allDocIds, skipped: allSkipped, folders }
}

// ── Manifest upload with per-file audit trail ──────────────────────────────

/**
 * Terminal status for a single file after `uploadManifest` processes it.
 *
 * - `uploaded`  — new document created on the backend
 * - `duplicate` — server reported it as a duplicate of an existing doc
 * - `rejected`  — server refused (unsupported on server, too large, etc.)
 *                 or caller-side validation failed (file missing, wrong type,
 *                 oversized, read error)
 * - `error`     — transport / unexpected failure, safe to retry
 * - `skipped`   — caller-side skip without an attempt (unsupported extension,
 *                 already completed in a previous run, etc.)
 */
export type FileStatus = 'uploaded' | 'duplicate' | 'rejected' | 'error' | 'skipped'

/** Per-file outcome emitted by `uploadManifest`. */
export interface FileOutcome {
  /** The caller-supplied path (as passed into `uploadManifest`). */
  path: string
  /** The sanitized folder used for the backend `folder` query param. */
  folder: string
  /** File size at resolution time, in bytes. */
  sizeBytes: number
  /** Terminal status for this file. */
  status: FileStatus
  /** Backend doc ext id, populated on `uploaded` and `duplicate`. */
  docId?: string
  /** Free-form reason, populated on any non-`uploaded` status. */
  reason?: string
  /** 1-indexed upload attempt that produced this outcome. 0 for caller-side. */
  attempt: number
  /** Backend batch id, when known. */
  batchId?: string | null
}

export interface UploadManifestProgress {
  done: number
  total: number
  bytesDone: number
  bytesTotal: number
}

export interface UploadManifestOptions {
  /**
   * Root directory used to derive the `folder` query param for each file.
   * If omitted, the longest common parent directory of all inputs is used.
   */
  rootDir?: string
  /** Config ext id to pass with each batch (e.g. SKIP_DUPLICATES config). */
  configExtId?: string
  /** Max upload attempts for transient errors. Default 3. */
  maxRetries?: number
  /**
   * Called once for every input path with its terminal outcome, in input
   * order. Awaited, so callers can flush audit logs synchronously before
   * the next file is processed.
   */
  onFile?: (outcome: FileOutcome) => void | Promise<void>
  /** Called after each batch completes (or aborts). */
  onProgress?: (progress: UploadManifestProgress) => void
  /** Stop processing further files on first `error` outcome. */
  failFast?: boolean
  /** Abort signal. Remaining files are emitted as `error` with reason `aborted`. */
  signal?: AbortSignal
}

export interface UploadManifestSummary {
  total: number
  uploaded: number
  duplicate: number
  rejected: number
  error: number
  skipped: number
}

export interface UploadManifestResult {
  outcomes: FileOutcome[]
  summary: UploadManifestSummary
}

interface PreparedFile {
  inputPath: string
  absolutePath: string
  basename: string
  folder: string
  size: number
}

/**
 * Compute the longest common parent directory of a set of absolute paths.
 * Returns the filesystem root when no common prefix exists.
 */
function commonParentDir(absolutePaths: string[]): string {
  if (absolutePaths.length === 0) return path.sep
  if (absolutePaths.length === 1) return path.dirname(absolutePaths[0])
  const split = absolutePaths.map((p) => path.dirname(p).split(path.sep))
  const minLen = Math.min(...split.map((s) => s.length))
  const common: string[] = []
  for (let i = 0; i < minLen; i++) {
    const seg = split[0][i]
    if (split.every((s) => s[i] === seg)) common.push(seg)
    else break
  }
  const joined = common.join(path.sep)
  return joined || path.sep
}

function deriveFolder(absolutePath: string, rootDir: string, rootBasename: string): string {
  const parent = path.dirname(absolutePath)
  const rel = path.relative(rootDir, parent)
  let folderPath: string
  if (rel === '' || rel === '.') {
    folderPath = rootBasename
  } else if (rel.startsWith('..')) {
    // File lives outside rootDir — fall back to its immediate parent basename.
    folderPath = path.basename(parent) || rootBasename
  } else {
    folderPath = path.join(rootBasename, rel)
  }
  return sanitizeFolderPath(folderPath.split(path.sep).join('/'))
}

function classifySkipReason(reason: string): 'duplicate' | 'rejected' {
  const lower = reason.toLowerCase()
  if (lower.includes('duplicate') || lower.includes('already exists')) {
    return 'duplicate'
  }
  return 'rejected'
}

function isTransientError(err: unknown): boolean {
  if (!(err instanceof Error)) return false
  const msg = err.message.toLowerCase()
  if (msg.includes('econnreset') || msg.includes('etimedout') || msg.includes('econnrefused')) {
    return true
  }
  if (msg.includes('fetch failed') || msg.includes('network')) return true
  if (/\b5\d\d\b/.test(msg)) return true // 5xx in error text
  return false
}

function sleep(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms))
}

/**
 * Upload an explicit list of file paths with a per-file audit trail.
 *
 * This is the eDiscovery-grade primitive: every caller-supplied path produces
 * exactly one `FileOutcome`, emitted via `onFile` in input order, so the
 * caller can stream the outcomes to a JSONL log / CSV / state file with no
 * ambiguity about which input got which doc id.
 *
 * Per-file mapping is recovered from the backend's lumped
 * `{doc_ext_ids[], skipped[]}` response as follows:
 *
 *   1. Files are grouped by their parent folder (relative to `rootDir` or
 *      the longest common parent of all inputs). Within one filesystem
 *      folder, basenames are guaranteed unique, which is what makes the
 *      reconciliation below safe.
 *   2. Each folder is chunked into batches of UPLOAD_BATCH_SIZE.
 *   3. After each batch request: any basename in `skipped[]` maps back to
 *      its input path by basename lookup; the remaining survivors are
 *      matched positionally against `doc_ext_ids[]`.
 *
 * If the backend's response length disagrees with the survivor count, all
 * survivors are emitted as `error` so the caller can retry.
 */
export async function uploadManifest(
  auth: AuthHeaders,
  paths: string[],
  options?: UploadManifestOptions
): Promise<UploadManifestResult> {
  const maxRetries = Math.max(1, options?.maxRetries ?? 3)
  const outcomes: FileOutcome[] = []
  const summary: UploadManifestSummary = {
    total: paths.length,
    uploaded: 0,
    duplicate: 0,
    rejected: 0,
    error: 0,
    skipped: 0,
  }
  let abortedByFailFast = false

  const emit = async (o: FileOutcome): Promise<void> => {
    outcomes.push(o)
    summary[o.status] += 1
    if (options?.onFile) await options.onFile(o)
    if (options?.failFast && o.status === 'error') abortedByFailFast = true
  }

  // ── Step 1: resolve + validate + classify inputs ─────────────────────────
  const prepared: PreparedFile[] = []
  const absoluteInputs = paths.map((p) => path.resolve(p))
  const rootDir = options?.rootDir ? path.resolve(options.rootDir) : commonParentDir(absoluteInputs)
  const rootBasename = path.basename(rootDir) || 'root'

  for (let i = 0; i < paths.length; i++) {
    if (options?.signal?.aborted || abortedByFailFast) {
      await emit({
        path: paths[i],
        folder: '',
        sizeBytes: 0,
        status: 'error',
        reason: 'aborted',
        attempt: 0,
      })
      continue
    }

    const inputPath = paths[i]
    const abs = absoluteInputs[i]

    if (!fs.existsSync(abs)) {
      await emit({
        path: inputPath,
        folder: '',
        sizeBytes: 0,
        status: 'rejected',
        reason: 'file_not_found',
        attempt: 0,
      })
      continue
    }
    const stat = fs.statSync(abs)
    if (!stat.isFile()) {
      await emit({
        path: inputPath,
        folder: '',
        sizeBytes: stat.size,
        status: 'rejected',
        reason: 'not_a_regular_file',
        attempt: 0,
      })
      continue
    }

    const ext = path.extname(abs).toLowerCase()
    if (!SUPPORTED_EXTENSIONS.has(ext)) {
      await emit({
        path: inputPath,
        folder: '',
        sizeBytes: stat.size,
        status: 'skipped',
        reason: `unsupported_extension:${ext || 'none'}`,
        attempt: 0,
      })
      continue
    }

    if (stat.size > MAX_FILE_SIZE_BYTES) {
      const gb = (stat.size / (1024 * 1024 * 1024)).toFixed(2)
      await emit({
        path: inputPath,
        folder: '',
        sizeBytes: stat.size,
        status: 'rejected',
        reason: `too_large:${gb}GB`,
        attempt: 0,
      })
      continue
    }

    prepared.push({
      inputPath,
      absolutePath: abs,
      basename: path.basename(abs),
      folder: deriveFolder(abs, rootDir, rootBasename),
      size: stat.size,
    })
  }

  // ── Step 2: group by folder, batch, upload with reconciliation ──────────
  const groups = new Map<string, PreparedFile[]>()
  for (const pf of prepared) {
    if (!groups.has(pf.folder)) groups.set(pf.folder, [])
    groups.get(pf.folder)!.push(pf)
  }

  const totalBytes = prepared.reduce((s, p) => s + p.size, 0)
  let done = outcomes.length
  let bytesDone = 0

  for (const [folder, files] of groups) {
    const batches = chunk(files, UPLOAD_BATCH_SIZE)
    for (const batch of batches) {
      if (options?.signal?.aborted || abortedByFailFast) {
        for (const f of batch) {
          await emit({
            path: f.inputPath,
            folder: f.folder,
            sizeBytes: f.size,
            status: 'error',
            reason: 'aborted',
            attempt: 0,
          })
          done++
          bytesDone += f.size
        }
        options?.onProgress?.({ done, total: summary.total, bytesDone, bytesTotal: totalBytes })
        continue
      }

      // Read each file into a blob. A read error here is terminal for that
      // file (emitted immediately) so the rest of the batch still proceeds.
      const readable: Array<{ f: PreparedFile; blob: Blob }> = []
      for (const f of batch) {
        try {
          const blob = readFileAsBlob(f.absolutePath, f.size, f.basename)
          readable.push({ f, blob })
        } catch (err) {
          const msg = err instanceof Error ? err.message : String(err)
          await emit({
            path: f.inputPath,
            folder: f.folder,
            sizeBytes: f.size,
            status: 'error',
            reason: `read_error:${msg}`,
            attempt: 0,
          })
          done++
          bytesDone += f.size
        }
      }

      if (readable.length === 0) {
        options?.onProgress?.({ done, total: summary.total, bytesDone, bytesTotal: totalBytes })
        continue
      }

      // Upload with bounded retry on transient errors.
      let attempt = 0
      let result: UploadResult | undefined
      let lastErr: unknown
      while (attempt < maxRetries) {
        attempt++
        try {
          result = await uploadFiles(
            auth,
            readable.map((r) => ({ data: r.blob, name: r.f.basename })),
            { folder, configExtId: options?.configExtId }
          )
          lastErr = undefined
          break
        } catch (err) {
          lastErr = err
          if (!isTransientError(err) || attempt >= maxRetries) break
          await sleep(500 * 2 ** (attempt - 1))
        }
      }

      if (!result) {
        const reason = lastErr instanceof Error ? lastErr.message : String(lastErr ?? 'unknown')
        for (const r of readable) {
          await emit({
            path: r.f.inputPath,
            folder: r.f.folder,
            sizeBytes: r.f.size,
            status: 'error',
            reason: `upload_failed:${reason}`,
            attempt,
          })
          done++
          bytesDone += r.f.size
        }
        options?.onProgress?.({ done, total: summary.total, bytesDone, bytesTotal: totalBytes })
        continue
      }

      // Reconcile: basename lookup for skipped entries, positional match
      // for the rest.
      const skippedByName = new Map<string, string>()
      for (const s of result.skipped ?? []) {
        skippedByName.set(s.file_name, s.reason)
      }
      const docIds = result.doc_ext_ids ?? []
      const batchId = result.batch_id ?? null

      const survivors: PreparedFile[] = []
      for (const { f } of readable) {
        const skipReason = skippedByName.get(f.basename)
        if (skipReason !== undefined) {
          const status = classifySkipReason(skipReason)
          await emit({
            path: f.inputPath,
            folder: f.folder,
            sizeBytes: f.size,
            status,
            reason: skipReason,
            attempt,
            batchId,
          })
          done++
          bytesDone += f.size
        } else {
          survivors.push(f)
        }
      }

      if (docIds.length !== survivors.length) {
        for (const f of survivors) {
          await emit({
            path: f.inputPath,
            folder: f.folder,
            sizeBytes: f.size,
            status: 'error',
            reason: `response_mismatch: got ${docIds.length} ids for ${survivors.length} survivors`,
            attempt,
            batchId,
          })
          done++
          bytesDone += f.size
        }
      } else {
        for (let i = 0; i < survivors.length; i++) {
          const f = survivors[i]
          await emit({
            path: f.inputPath,
            folder: f.folder,
            sizeBytes: f.size,
            status: 'uploaded',
            docId: docIds[i],
            attempt,
            batchId,
          })
          done++
          bytesDone += f.size
        }
      }

      options?.onProgress?.({ done, total: summary.total, bytesDone, bytesTotal: totalBytes })
    }
  }

  return { outcomes, summary }
}

/**
 * Recursively walk a directory and return all absolute paths of files whose
 * extension is in SUPPORTED_EXTENSIONS. Useful for turning a directory input
 * into a flat manifest before calling `uploadManifest`.
 */
export function walkSupportedFiles(dirPath: string): string[] {
  const out: string[] = []
  const resolved = path.resolve(dirPath)
  const stack: string[] = [resolved]
  while (stack.length > 0) {
    const current = stack.pop()!
    let entries: fs.Dirent[]
    try {
      entries = fs.readdirSync(current, { withFileTypes: true })
    } catch {
      continue
    }
    for (const entry of entries) {
      const full = path.join(current, entry.name)
      if (entry.isDirectory()) {
        stack.push(full)
      } else if (entry.isFile()) {
        const ext = path.extname(entry.name).toLowerCase()
        if (SUPPORTED_EXTENSIONS.has(ext)) out.push(full)
      }
    }
  }
  return out.sort()
}

// ── Archive extraction (7z, rar) ───────────────────────────────────────────

/**
 * Upload files from a 7z/rar archive, preserving internal folder structure.
 * Uses the system `7z` command to extract to a temp directory, then delegates
 * to `uploadDirectory`. Requires p7zip / 7z to be installed on the system.
 * Supports: .7z, .rar (anything `7z x` can handle).
 */
export async function uploadArchive(
  auth: AuthHeaders,
  archivePath: string,
  options?: UploadDirectoryOptions
): Promise<UploadBatchResult> {
  try {
    execSync('7z --help', { stdio: 'ignore' })
  } catch {
    throw new Error('7z is not installed. Install p7zip-full to extract archives.')
  }

  const tmpDir = fs.mkdtempSync(path.join(os.tmpdir(), 'arbi-archive-'))
  try {
    execSync(`7z x -y -o"${tmpDir}" "${path.resolve(archivePath)}"`, { stdio: 'ignore' })

    const topLevel = fs.readdirSync(tmpDir)
    let uploadDir: string
    if (topLevel.length === 1 && fs.statSync(path.join(tmpDir, topLevel[0])).isDirectory()) {
      uploadDir = path.join(tmpDir, topLevel[0])
    } else {
      const baseName = path.basename(archivePath).replace(/\.(7z|rar|zip)$/i, '')
      const namedDir = path.join(path.dirname(tmpDir), baseName)
      fs.renameSync(tmpDir, namedDir)
      uploadDir = namedDir
    }

    return await uploadDirectory(auth, uploadDir, options)
  } finally {
    fs.rmSync(tmpDir, { recursive: true, force: true })
  }
}
